// shared/API.md §46 in the info panel: a group's shared alias (any member) and a channel's type
// (channel admins and system admins). The server is the judge of both; an answer is stored at once by
// the worker, so the sidebar, header and switcher follow without a refetch.
import { useEffect, useState } from 'react';
import { data } from '../../../bridge/client';
import { ChannelMeta, type ChannelMetaOp, type ChannelMetaState } from '../../../core/channelMeta';
import type { ChannelRecord } from '../../../core/records';
import { t } from '../../../i18n/i18n';
import { Icon } from '../../../design/icons';
import { useShell } from '../../shell/ShellContext';

export async function writeChannelMeta(op: ChannelMetaOp, toast: (text: string, kind: 'error') => void): Promise<ChannelMetaState | null> {
  try {
    const res = await data.call('channelMetaWrite', op);
    if (res.ok) {
      ChannelMeta.setCurrent(res.state);
      return res.state;
    }
    toast(t(ChannelMeta.errorKey(res.error)), 'error');
  } catch {
    toast(t('channelMeta.error.generic'), 'error');
  }
  return null;
}

/** «Alias del grupo»: a field that saves on Enter or when it loses focus; empty clears it. */
export function GroupAliasRow({ channel }: { channel: ChannelRecord }) {
  const shell = useShell();
  const saved = ChannelMeta.currentAlias(channel.id) ?? '';
  const [value, setValue] = useState(saved);
  const [busy, setBusy] = useState(false);
  useEffect(() => setValue(ChannelMeta.currentAlias(channel.id) ?? ''), [channel.id]);

  const save = async (next: string) => {
    const clean = ChannelMeta.cleanName(next);
    if (busy || clean === (ChannelMeta.currentAlias(channel.id) ?? '')) {
      setValue(clean);
      return;
    }
    setBusy(true);
    const state = await writeChannelMeta({ kind: 'alias', channelId: channel.id, alias: clean }, shell.toast);
    setValue(state ? ChannelMeta.aliasFor(state, channel.id) ?? '' : ChannelMeta.currentAlias(channel.id) ?? '');
    setBusy(false);
  };

  return (
    <div className="cp-meta-row" data-group-alias>
      <span className="list-row-leading">
        <Icon name="pencilLine" size={18} />
      </span>
      <label className="cp-meta-field">
        <span className="cp-meta-label">{t('channelMeta.alias.title')}</span>
        <input
          className="text-input"
          value={value}
          maxLength={ChannelMeta.MAX_ALIAS}
          disabled={busy}
          placeholder={t('channelMeta.alias.placeholder')}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => void save(value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              setValue(ChannelMeta.currentAlias(channel.id) ?? '');
            }
          }}
        />
        <span className="cp-meta-hint">{t('channelMeta.alias.hint')}</span>
      </label>
    </div>
  );
}

/** «Tipo»: the channel's type; a select for whoever may change it, a plain line for everyone else. */
export function ChannelTypeRow({ channel, canManage }: { channel: ChannelRecord; canManage: boolean }) {
  const shell = useShell();
  const [state, setState] = useState<ChannelMetaState>(ChannelMeta.currentState());
  const [busy, setBusy] = useState(false);
  useEffect(() => setState(ChannelMeta.currentState()), [channel.id]);
  const current = ChannelMeta.typeFor(state, channel.id);
  if (!state.types.length) return null;
  if (!canManage && !current) return null;

  const pick = async (typeId: string) => {
    if (busy || typeId === (current?.id ?? '')) return;
    setBusy(true);
    const next = await writeChannelMeta({ kind: 'channelType', channelId: channel.id, typeId: typeId || null }, shell.toast);
    if (next) setState(next);
    setBusy(false);
  };

  return (
    <div className="cp-meta-row" data-channel-type>
      <span className="list-row-leading">
        {current ? <span className="channel-type-dot cp-meta-dot" data-color={current.color} aria-hidden="true" /> : <Icon name="stack" size={18} />}
      </span>
      <label className="cp-meta-field">
        <span className="cp-meta-label">{t('channelMeta.type.title')}</span>
        {canManage ? (
          <select className="text-input" value={current?.id ?? ''} disabled={busy} onChange={(e) => void pick(e.target.value)}>
            <option value="">{t('channelMeta.type.none')}</option>
            {state.types.map((ty) => (
              <option key={ty.id} value={ty.id}>
                {ty.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="cp-meta-value">{current?.name}</span>
        )}
      </label>
    </div>
  );
}
